'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';

interface CookiePrefs {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = 'cookie-consent';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>({
    necessary: true,
    analytics: true,
    marketing: true,
  });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      setShowBanner(true);
    } else {
      try {
        const parsed = JSON.parse(saved) as CookiePrefs;
        setPrefs({ ...parsed, necessary: true });
      } catch {
        setShowBanner(true);
      }
    }

    const handleOpen = () => {
      setShowSettings(true);
    };
    window.addEventListener('open-cookie-settings', handleOpen);
    return () => window.removeEventListener('open-cookie-settings', handleOpen);
  }, []);

  const save = (newPrefs: CookiePrefs) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newPrefs));
    setPrefs(newPrefs);
    setShowBanner(false);
    setShowSettings(false);
    window.dispatchEvent(new Event('cookie-consent-changed'));
  };

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, analytics: false, marketing: false });
  const saveCurrent = () => save(prefs);

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {showBanner && !showSettings && (
        <div className="cookie-banner" role="dialog" aria-label="Oznámení o cookies">
          <div className="cookie-banner-inner">
            <div className="cookie-banner-text">
              <h3>Tento web používá cookies</h3>
              <p>
                Cookies používáme k zajištění správné funkčnosti webu, analýze návštěvnosti a personalizaci obsahu.
                <Link href="/cookies" className="cookie-link"> Více informací</Link>
              </p>
            </div>
            <div className="cookie-banner-actions">
              <button type="button" onClick={rejectAll} className="cookie-btn cookie-btn-ghost">
                Odmítnout vše
              </button>
              <button type="button" onClick={() => setShowSettings(true)} className="cookie-btn cookie-btn-outline">
                Nastavení
              </button>
              <button type="button" onClick={acceptAll} className="cookie-btn cookie-btn-primary">
                Přijmout vše
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="cookie-modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="cookie-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="cookie-modal-title">
            <div className="cookie-modal-header">
              <h2 id="cookie-modal-title">Nastavení cookies</h2>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="cookie-modal-close"
                aria-label="Zavřít"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="cookie-modal-body">
              <div className="cookie-category">
                <div className="cookie-category-text">
                  <h3>Nezbytné cookies</h3>
                  <p>Zajišťují základní funkčnost webu, jako je navigace a přístup k zabezpečeným oblastem. Web bez nich nemůže správně fungovat.</p>
                </div>
                <Toggle checked={true} disabled />
              </div>

              <div className="cookie-category">
                <div className="cookie-category-text">
                  <h3>Analytické cookies</h3>
                  <p>Pomáhají nám pochopit, jak návštěvníci používají web. Díky nim můžeme zlepšovat funkčnost a obsah stránek.</p>
                </div>
                <Toggle
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                />
              </div>

              <div className="cookie-category">
                <div className="cookie-category-text">
                  <h3>Marketingové cookies</h3>
                  <p>Používají se k sledování návštěvníků napříč webovými stránkami za účelem zobrazení relevantních reklam.</p>
                </div>
                <Toggle
                  checked={prefs.marketing}
                  onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                />
              </div>
            </div>

            <div className="cookie-modal-footer">
              <Link href="/cookies" className="cookie-link">Více informací o cookies</Link>
              <div className="cookie-modal-actions">
                <button type="button" onClick={acceptAll} className="cookie-btn cookie-btn-primary">
                  Přijmout vše
                </button>
                <button type="button" onClick={saveCurrent} className="cookie-btn cookie-btn-outline">
                  Uložit nastavení
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={`cookie-toggle${checked ? ' checked' : ''}${disabled ? ' disabled' : ''}`}
    >
      <span className="cookie-toggle-dot" />
    </button>
  );
}
